import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';

import { VStack } from '@/components/Layout';

import { usePackage } from './-hooks/usePackage';
import { listOperationPackages, listPackagePatterns } from './-query/api';
import { SearchSchema } from './-zod-schemas';

/**
 * IT Operation Coreのパッケージの詳細な表示を行うコンポーネント。
 * サイドバー以外のDataTable部分を表示することを主な責務とする。
 * 個別の packageType ごとにディレクトリを分ける手もあったが、Operation Package自体が汎用的な機構であるため、
 * できる限りその設計思想に乗っ取るようにした。
 */
export const Route = createFileRoute(
  '/_staff/it-operation-core/_root/packages/$packageId',
)({
  loader: async ({ params }) => {
    // パッケージパターンと全パッケージリストの両方を取得
    const [packagePatterns, allPackages] = await Promise.all([
      listPackagePatterns(params.packageId),
      listOperationPackages(),
    ]);

    return {
      packagePatterns,
      allPackages,
      packageId: params.packageId,
    };
  },
  validateSearch: zodValidator(SearchSchema),
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { packageId } = Route.useParams();
  const search = Route.useSearch();
  const { patternId } = search;

  const { packagePatterns, allPackages } = Route.useLoaderData();

  // ローダーで取得したデータから該当パッケージを検索
  const operationPackage = Object.values(allPackages)
    .flat()
    .find((p) => p.id === packageId);

  if (!operationPackage) {
    throw new Error(`Operation package not found for ID: ${packageId}`);
  }

  // PackageTypeごとに表示するコンポーネントを切り替える
  const { Component } = usePackage({ packageType: operationPackage.type });

  useEffect(() => {
    if (!patternId && packagePatterns.length > 0) {
      navigate({
        to: '/it-operation-core/packages/$packageId',
        params: { packageId },
        search: {
          ...search,
          patternId: packagePatterns[0].operationPackagePatternId,
        },
      });
    }
  }, [packagePatterns, patternId, navigate, packageId, search]);

  return (
    <VStack className="h-full w-full">
      <Component
        operationPackage={operationPackage}
        packagePatterns={packagePatterns}
        patternId={patternId}
        searchSchema={search}
      />
    </VStack>
  );
}
